import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import Tabs, { Item as TabItem } from 'devextreme-react/tabs';
import TabPanel, { Item as TabPanelItem } from 'devextreme-react/tab-panel';

import Box, { Item as BoxItem } from 'devextreme-react/box';

import './index.scss';
import { AntGlobal, AntSession } from '../../../utils/AntContext';
import AntAPI from '../../../utils/AntAPI';
import AntCondition from '../../../utils/AntCondition';
import AntLocalization from '../../../utils/AntLocalization';

import { useNavigate } from 'react-router-dom';

import AntPOSSearchList from '../../AntPOSSearchList';
import AntPOSSearchGrid from '../../AntPOSSearchGrid';

import AntPOSSearchScheduler from '../../AntPOSSearchScheduler';

export default forwardRef(function (props, ref) {
    const navigate = useNavigate();
    const appGlobal = AntGlobal();
    const session = AntSession();

    const [shouldRender, setShouldRender] = useState(1);
    const [shouldQuery, setShouldQuery] = useState(1);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);
    const isQueryNow = useMemo(() => shouldQuery + (props.shouldQuery ? props.shouldQuery : 1), [props.shouldQuery, shouldQuery]);

    const tabResources = useRef(new Map());
    const tabRefs = useMemo(() => {
        const activeNames = new Set();
        const refs = (props.tabs || []).map((tab) => {
            activeNames.add(tab.name);
            const apiSignature = `${tab.module || ''}|${tab.table || ''}`;
            let resource = tabResources.current.get(tab.name);
            if (!resource) {
                resource = {
                    name: tab.name,
                    ref: React.createRef(),
                    api: new AntAPI(tab.module, tab.table),
                    collectionSnapshotStates: tab.collectionSnapshotStates instanceof Map
                        ? tab.collectionSnapshotStates
                        : new Map(),
                    apiSignature
                };
                tabResources.current.set(tab.name, resource);
            } else if (resource.apiSignature !== apiSignature) {
                resource.api = new AntAPI(tab.module, tab.table);
                resource.apiSignature = apiSignature;
            }
            if (tab.collectionSnapshotStates instanceof Map) {
                resource.collectionSnapshotStates = tab.collectionSnapshotStates;
            }
            return resource;
        });
        [...tabResources.current.keys()].forEach((name) => {
            if (!activeNames.has(name)) tabResources.current.delete(name);
        });
        return refs;
    }, [props.tabs]);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const forceQuery = useCallback(() => {
        setShouldQuery((v) => v + 1);
    }, []);

    var bgImage = props.backgroundImage
        ? props.backgroundImage
        : appGlobal && appGlobal.appAssets && appGlobal.appAssets.images.searchPageBackground
        ? appGlobal.appAssets.images.searchPageBackground
        : props.backgroundImage;

    const [utils, setUtils] = useState(props.tabs ? props.tabs : []);
    const [selectedUtil, setSelectedUtil] = useState(props.tabs && props.tabs.length > 0 ? props.tabs[0] : null);
    const [selectedUtilIndex, setSelectedUtilIndex] = useState(0);
    const [activatedTabNames, setActivatedTabNames] = useState(
        () => new Set(props.tabs && props.tabs[0]?.name ? [props.tabs[0].name] : [])
    );

    const activateTab = useCallback((tabName) => {
        if (!tabName) return;
        setActivatedTabNames((current) => {
            if (current.has(tabName)) return current;
            const next = new Set(current);
            next.add(tabName);
            return next;
        });
    }, []);

    // Tên tab đang mở, tách riêng khỏi cả object: khuôn dựng nội dung tab chỉ cần cái tên.
    // Nhờ vậy làm mới danh sách tab (đổi danh tính object) không kéo theo dựng lại mọi tab.
    const selectedUtilName = selectedUtil ? selectedUtil.name : null;
    const selectedUtilRef = useRef(null);
    selectedUtilRef.current = selectedUtil;
    const isTabForeground = useCallback((tabName) => {
        if (!tabName || selectedUtilRef.current?.name !== tabName) return false;
        return typeof document === 'undefined' || document.visibilityState !== 'hidden';
    }, []);

    // Nav badges (port từ AntNavigationMenuV2): tab có navBadge sẽ poll số phiếu
    // theo searchConditions / searchGroup và hiển thị bên cạnh title.
    const [badges, setBadges] = useState({});
    const badgeTasks = useMemo(() => {
        const ar = props.tabs || [];
        return ar.filter((t) => t && t.navBadge && t.navBadge.table).map((t) => ({ key: t.name, navBadge: t.navBadge }));
    }, [props.tabs]);
    const badgesFetcherRef = useRef(null);

    useEffect(() => {
        if (!badgeTasks.length) {
            setBadges({});
            badgesFetcherRef.current = null;
            return undefined;
        }
        let cancelled = false;
        const SG = appGlobal && appGlobal.appConfig && appGlobal.appConfig.SearchGroups;
        const resolveConditions = (nb) => {
            if (!nb || !nb.table) return null;
            if (nb.searchConditions) return nb.searchConditions;
            if (!SG || !nb.searchGroup) return null;
            const entry = SG.find ? SG.find((g) => g.name === nb.table) : null;
            const groups = entry && entry.value;
            if (!(groups instanceof Array)) return null;
            const grp = groups.find((g) => g.name === nb.searchGroup);
            return grp && grp.searchConditions ? grp.searchConditions : null;
        };
        const fetchBadges = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return Promise.resolve();
            const results = {};
            if (props.badgeBatch && props.badgeBatch.action) {
                const batchAPI = new AntAPI(props.badgeBatch.module || 'user', props.badgeBatch.table || 'ACUserBadge');
                return batchAPI
                    .postActionWithData(props.badgeBatch.action, { tabs: badgeTasks.map((task) => task.key) })
                    .then((response) => {
                        const data = response && response.data && response.data.counts ? response.data : response;
                        const counts = (data && data.counts) || {};
                        badgeTasks.forEach((task) => {
                            results[task.key] = typeof counts[task.key] === 'number' ? counts[task.key] : 0;
                        });
                        if (!cancelled) setBadges(results);
                    })
                    .catch(() => {
                        badgeTasks.forEach((task) => {
                            results[task.key] = 0;
                        });
                        if (!cancelled) setBadges(results);
                    });
            }
            const promises = badgeTasks.map((task) => {
                const conds = resolveConditions(task.navBadge);
                if (!conds) return Promise.resolve();
                try {
                    const api = new AntAPI(task.navBadge.module, task.navBadge.table);
                    return api
                        .getCount(conds)
                        .then((resp) => {
                            let n = 0;
                            if (typeof resp === 'number') n = resp;
                            else if (resp && typeof resp.count === 'number') n = resp.count;
                            else if (resp && typeof resp.data === 'number') n = resp.data;
                            else if (resp && resp.data && typeof resp.data.count === 'number') n = resp.data.count;
                            results[task.key] = n || 0;
                        })
                        .catch(() => {
                            results[task.key] = 0;
                        });
                } catch (e) {
                    results[task.key] = 0;
                    return Promise.resolve();
                }
            });
            Promise.all(promises).then(() => {
                if (!cancelled) setBadges(results);
            });
        };
        badgesFetcherRef.current = fetchBadges;
        fetchBadges();
        const intervalSec = props.badgeRefreshSeconds > 0 ? props.badgeRefreshSeconds : 30;
        const handle = setInterval(fetchBadges, intervalSec * 1000);
        return () => {
            cancelled = true;
            badgesFetcherRef.current = null;
            clearInterval(handle);
        };
    }, [badgeTasks, appGlobal, props.badgeRefreshSeconds, props.badgeBatch]);

    const refreshBadges = useCallback(() => {
        if (badgesFetcherRef.current) badgesFetcherRef.current();
    }, []);

    const patchTabRows = useCallback(
        (tabName, rows, options) => {
            const tabRef = tabRefs.find((item) => item.name === tabName)?.ref;
            if (!tabRef?.current?.patchRows) return false;
            tabRef.current.patchRows(rows, options);
            return true;
        },
        [tabRefs]
    );

    const getTabSearchConditions = useCallback(
        (tabName) => {
            const tabRef = tabRefs.find((item) => item.name === tabName)?.ref;
            return tabRef?.current?.getSearchConditions?.() || null;
        },
        [tabRefs]
    );

    const refreshTabRows = useCallback(
        (tabName, searchPatch, options) => {
            if (!isTabForeground(tabName)) return Promise.resolve([]);
            const tabRef = tabRefs.find((item) => item.name === tabName)?.ref;
            if (!tabRef?.current?.refreshRows) return Promise.resolve([]);
            return tabRef.current.refreshRows(searchPatch, options);
        },
        [isTabForeground, tabRefs]
    );

    const isTabReady = useCallback(
        (tabName) => {
            if (!isTabForeground(tabName)) return false;
            if (!activatedTabNames.has(tabName)) return false;
            const tabRef = tabRefs.find((item) => item.name === tabName)?.ref;
            return Boolean(tabRef?.current?.isReady?.());
        },
        [activatedTabNames, isTabForeground, tabRefs]
    );

    const badgeDebounceRef = useRef(null);
    const badgeInitialMountRef = useRef(true);
    useEffect(() => {
        if (badgeInitialMountRef.current) {
            badgeInitialMountRef.current = false;
            return undefined;
        }
        if (!selectedUtil || !badgesFetcherRef.current) return undefined;
        if (badgeDebounceRef.current) clearTimeout(badgeDebounceRef.current);
        badgeDebounceRef.current = setTimeout(() => {
            if (badgesFetcherRef.current) badgesFetcherRef.current();
        }, 400);
        return () => {
            if (badgeDebounceRef.current) clearTimeout(badgeDebounceRef.current);
        };
    }, [selectedUtil]);

    const itemTitleRender = useCallback((data) => {
        let ar = ['ant-pos-screen-util-header'];
        if (Boolean(data?.isClicked)) ar.push('ant-pos-screen-util-header-focused');
        const n = data && data.name ? badges[data.name] : 0;
        return (
            <div className={ar.join(' ')}>
                {AntLocalization.translateText(AntCondition.getValue({}, data.title))}
                {n > 0 ? <span className="ant-pos-screen-util-badge">{n > 99 ? '99+' : n}</span> : null}
            </div>
        );
    }, [badges]);

    const itemRender = useCallback(
        (data) => {
            // DevExtreme can invoke itemRender for every item even with
            // deferRendering enabled. Guard the content explicitly so a custom
            // tab cannot issue API calls before the user opens it.
            if (!activatedTabNames.has(data.name)) return null;

            const resource = tabRefs.find((r) => r.name === data.name);
            const API = resource?.api || new AntAPI(data.module, data.table);
            const iRef = resource?.ref;

            // Custom tab — caller cung cap render() de tu render noi dung tab.
            // Khong impact cac type khac (SearchList/SearchGrid/SearchScheduler).
            if (data.type === 'Custom' && typeof data.render === 'function') {
                return (
                    <Box
                        id={data.name}
                        key={data.name}
                        elementAttr={{ class: 'ant-pos-screen-util-content' }}
                        direction="col"
                        width="100%"
                        height="100%"
                    >
                        <BoxItem ratio={1}>
                            {data.render({
                                data,
                                tabRef: iRef,
                                isForeground: Boolean(selectedUtilName && selectedUtilName === data.name),
                                onCellClick: props.onCellClick,
                                onUtilAction: props.onUtilAction,
                                afterUtilAction: props.afterUtilAction
                            })}
                        </BoxItem>
                    </Box>
                );
            }

            if (data.type === 'SearchList')
                return (
                    <Box
                        id={data.name}
                        key={data.name}
                        elementAttr={{
                            class: 'ant-pos-screen-util-content'
                        }}
                        style={{ position: 'relative' }}
                        direction="col"
                        width="100%"
                        height="100%"
                    >
                        <BoxItem ratio={1}>
                            <AntPOSSearchList
                                {...data}
                                ref={iRef}
                                module={data.module}
                                table={data.table}
                                API={data.API ? data.API : API}
                                collectionSnapshotStates={resource?.collectionSnapshotStates}
                                cardview={data.cardview}
                                isSearching={true}
                                shouldQuery={isQueryNow}
                                defaultPageSize={data.defaultPageSize ? data.defaultPageSize : 50}
                                isForeground={Boolean(selectedUtilName && selectedUtilName === data.name)}
                                showLoading={data.cardview?.showLoading}
                                showActions={data.cardview?.showActions}
                                autoShowActions={data.cardview?.autoShowActions}
                                refreshAfterAction={data.cardview?.refreshAfterAction}
                                rowActionPack={data.cardview?.rowActionPack}
                                rowActions={data.cardview?.rowActions ? data.cardview?.rowActions : data.rowActions}
                                excludesRowActions={data.cardview?.excludesRowActions}
                                onCellClick={props.onCellClick}
                                onRowAction={props.onUtilAction}
                                afterRowAction={props.afterUtilAction}
                                onScanBarcode={data.onScanBarcode ? data.onScanBarcode : props.onScanBarcode}
                                autoRefreshSeconds={data.autoRefreshSeconds ? data.autoRefreshSeconds : props.autoRefreshSeconds}
                                renderFloatingAction={data.renderFloatingAction}
                            ></AntPOSSearchList>
                        </BoxItem>
                        {/* <BoxItem >sdfdsf ddf </BoxItem> */}
                    </Box>
                );
            if (data.type === 'SearchGrid')
                return (
                    <Box
                        id={data.name}
                        key={data.name}
                        elementAttr={{
                            class: 'ant-pos-screen-util-content'
                        }}
                        direction="col"
                        width="100%"
                        height="100%"
                    >
                        <BoxItem ratio={1}>
                            <AntPOSSearchGrid
                                {...data}
                                ref={iRef}
                                isSearching={true}
                                module={data.module}
                                table={data && data.table ? data.table : data.API ? data.API.entityName : props.table}
                                API={data.API ? data.API : API}
                                url={data.url}
                                isForeground={Boolean(selectedUtilName && selectedUtilName === data.name)}
                                // cardview={data.cardview}
                                // height={tabGrid.height !== undefined ? tabGrid.height : props.tabContentHeight !== undefined ? props.tabContentHeight : 'inherit'}
                                // height={'100%'}
                                // height={0.55*window.innerHeight}

                                cellStyles={data ? data.cellStyles : undefined}
                                rowStyles={data ? data.rowStyles : undefined}
                                showRowActions={data ? data.showRowActions : undefined}
                                rowActionPack={data ? data.rowActionPack : undefined}
                                rowActions={data ? data.rowActions : undefined}
                                rowActionWidth={data ? data.rowActionWidth : undefined}
                                afterRowAction={props.onAfterAction}
                                keyExpr={'id'}
                                // remotePaging={data.remotePaging !== undefined ? data.remotePaging : false}
                                remotePaging={true}
                                // remotePaging={true}
                                allowedPageSizes={data.allowedPageSizes !== undefined ? data.allowedPageSizes : 5}
                                defaultPageSize={data.defaultPageSize ? data.defaultPageSize : 50}
                                showPageSizeSelector={false}
                                pagerHideable={true}
                                sortable={data.sortable !== undefined ? data.sortable : false}
                                selectable={false}
                                allowEdit={false}
                                focusStateEnabled={false}
                                fields={data.fields}
                                columns={data.columns}
                                rawColumns={data.columns}
                                keyboardNavigation={data.keyboardNavigation}
                                // actionItems={dataActions}
                                // onAction={data.onAction}
                                // onRowAction={data.onRowAction}
                                ordering={data.ordering}
                                searchConditions={data.searchConditions}
                                // searchFields={searchFields.length > 0 ? searchFields : undefined}
                                // searchConditionsRequired={true}
                                // isAllowQuery={() => isGridAllowQuery(data)}
                                // onDataSourceEdited={props.onDataSourceEdited}
                                showRefresh={data.showRefresh !== undefined ? data.showRefresh : true}
                                showActionBar={data.showActionBar}
                                // edit={data.edit}
                                // add={data.add}
                                // delete={data.delete}
                                // import={data.import}
                                // showEdit={props.disabled ? false : Boolean(showEdit)}
                                // showDelete={props.disabled ? false : Boolean(showDelete)}
                                // showAdd={props.disabled ? false : Boolean(showAdd)}
                                // showImport={props.disabled ? false : Boolean(showImport)}
                                // showDownload={data.showDownload}
                                showDetail={data.showDetail}
                                showIndex={data.showIndex}
                                // showSelectAll={data.showSelectAll !== undefined ? data.showSelectAll : true}
                                // showFilterBySelected={data.showFilterBySelected !== undefined ? data.showFilterBySelected : false}
                                // showFieldChooser={data.showFieldChooser !== undefined ? data.showFieldChooser : true}
                                showSearchBar={data.showSearchBar !== undefined ? data.showSearchBar : false}
                                autoFocusSearchBar={data.autoFocusSearchBar}
                                // showScanBarcode={!props.disabled && isCanAdd && showScanBarcode}
                                searchBarWidth={data.searchBarWidth}
                                onCellClick={props.onCellClick}
                                // onRowDblClick={(e) => {
                                //     if (props.onRowDblClick) props.onRowDblClick(e, tabName, data);
                                // }}
                                // onBarcodeScanned={props.onBarcodeScanned}
                                // onDeleted={props.onAfterAction}
                                // onAdded={props.onAfterAction}
                                // onEdited={props.onAfterAction}
                                // onImported={props.onAfterAction}
                                // canImportCreate={isCanImportCreate}
                                // canImportUpdate={isCanImportUpdate}
                                showDetailPosition={'right'}
                                searchCategories1={data.searchCategories1}
                                searchCategories2={data.searchCategories2}
                                searchCategories3={data.searchCategories3}
                                searchCategories4={data.searchCategories4}
                                searchCategories5={data.searchCategories5}
                                searchCategories6={data.searchCategories6}
                                searchCategories7={data.searchCategories7}
                                searchCategories8={data.searchCategories8}
                                searchCategories9={data.searchCategories9}
                                searchCategories10={data.searchCategories10}
                                byPassCachingStates={data.byPassCachingStates !== undefined ? data.byPassCachingStates : true}
                            ></AntPOSSearchGrid>
                        </BoxItem>
                        {/* <BoxItem >sdfdsf ddf </BoxItem> */}
                    </Box>
                );

            if (data.type === 'SearchScheduler')
                return (
                    <Box
                        id={data.name}
                        key={data.name}
                        elementAttr={{
                            class: 'ant-pos-screen-util-content'
                        }}
                        direction="col"
                        width="100%"
                        height="100%"
                    >
                        <BoxItem ratio={1}>
                            <AntPOSSearchScheduler
                                {...data}
                                ref={iRef}
                                isSearching={true}
                                module={data.module}
                                table={data && data.table ? data.table : data.API ? data.API.entityName : props.table}
                                API={data.API ? data.API : API}
                                url={data.url}
                                isForeground={Boolean(selectedUtilName && selectedUtilName === data.name)}
                                cellStyles={data ? data.cellStyles : undefined}
                                rowStyles={data ? data.rowStyles : undefined}
                                onCellClick={props.onCellClick}
                                onRowAction={props.onUtilAction}
                                showActions={data ? data.showActions : undefined}
                                rowActionPack={data ? data.rowActionPack : undefined}
                                rowActions={data ? data.rowActions : undefined}
                                rowActionWidth={data ? data.rowActionWidth : undefined}
                                afterRowAction={props.onAfterAction}
                                keyExpr={'id'}
                                remotePaging={true}
                                searchConditions={data.searchConditions}
                                showRefresh={data.showRefresh !== undefined ? data.showRefresh : true}
                                showActionBar={data.showActionBar}
                                showSearchBar={data.showSearchBar !== undefined ? data.showSearchBar : false}
                                autoFocusSearchBar={data.autoFocusSearchBar}
                                searchBarWidth={data.searchBarWidth}
                                showDetailPosition={'right'}
                                searchCategories1={data.searchCategories1}
                                searchCategories2={data.searchCategories2}
                                searchCategories3={data.searchCategories3}
                                searchCategories4={data.searchCategories4}
                                searchCategories5={data.searchCategories5}
                                searchCategories6={data.searchCategories6}
                                searchCategories7={data.searchCategories7}
                                searchCategories8={data.searchCategories8}
                                searchCategories9={data.searchCategories9}
                                searchCategories10={data.searchCategories10}
                                byPassCachingStates={data.byPassCachingStates !== undefined ? data.byPassCachingStates : true}
                            ></AntPOSSearchScheduler>
                        </BoxItem>
                        {/* <BoxItem >sdfdsf ddf </BoxItem> */}
                    </Box>
                );

            return (
                <div id={data.name} key={data.name} style={{ display: 'flex', justifyContent: 'center', marginTop: 150 }}>
                    <img
                        style={{
                            width: props.backgroundImageWidth ? props.backgroundImageWidth : 250,
                            opacity: props.backgroundImageOpacity ? props.backgroundImageOpacity : 0.5
                        }}
                        src={bgImage}
                    ></img>
                </div>
            );
        },
        [
            selectedUtilName,
            isQueryNow,
            tabRefs,
            props.onCellClick,
            props.onScanBarcode,
            props.autoRefreshSeconds,
            props.afterUtilAction,
            props.onUtilAction,
            activatedTabNames
        ]
    );

    const onSelectionChanged = useCallback(
        (e) => {
            const selected = e.addedItems[0];
            activateTab(selected?.name);
            setSelectedUtil(selected);
            if (utils?.length > 0) setSelectedUtilIndex(utils.findIndex((o) => o.name === selected?.name));
        },
        [activateTab, utils]
    );

    const selectTab = useCallback(
        (tabName) => {
            if (utils?.length > 0) {
                let uIndex = utils.findIndex((o) => o.name === tabName);
                let uTab = utils[uIndex];
                activateTab(uTab?.name);
                setSelectedUtil(uTab);
                setSelectedUtilIndex(uIndex);
            }
        },
        [activateTab, utils]
    );

    const refreshAll = useCallback(() => {
        refreshBadges();
        if (selectedUtil.name && selectedUtil?.type === 'SearchList') {
            let iRef = tabRefs.find((t) => t.name === selectedUtil.name);
            if (iRef?.ref) iRef = iRef.ref;
            if (iRef?.current) {
                if (selectedUtil.refreshHandledExternally !== true) iRef?.current.forceReload({ forceQuery: true });
                if (typeof selectedUtil.onRefreshClick === 'function') selectedUtil.onRefreshClick();
                return;
            }
        }
        setUtils([...utils]);
    }, [utils, selectedUtil, refreshBadges]);

    const focusSearchBox = useCallback(() => {
        if (selectedUtil.name && selectedUtil?.type === 'SearchList') {
            let iRef = tabRefs.find((t) => t.name === selectedUtil.name);
            if (iRef?.ref) iRef = iRef.ref;
            if (iRef?.current) {
                iRef?.current.focusSearchBox();
                return;
            }
        }
    }, [selectedUtil]);

    // const refreshAll = useCallback(() => {
    //     forceQuery();
    // }, [forceQuery]);

    const getSelectedTab = useCallback(() => {
        return selectedUtil;
    }, [selectedUtil]);

    useEffect(() => {
        setUtils(props.tabs ? props.tabs : []);
    }, [props.tabs]);

    useEffect(() => {
        if (!utils || utils.length === 0) {
            setSelectedUtil(null);
            setSelectedUtilIndex(0);
            return;
        }
        var prev = selectedUtilRef.current;
        var idx = prev ? utils.findIndex((u) => u.name === prev.name) : -1;
        if (idx < 0) idx = 0;
        activateTab(utils[idx]?.name);
        setSelectedUtil(utils[idx]);
        setSelectedUtilIndex(idx);
    }, [activateTab, utils]);

    useEffect(() => {
        const currentSelectedUtil = selectedUtilRef.current;
        if (currentSelectedUtil && props.onUtilTabChange) {
            props.onUtilTabChange(selectedUtilIndex, currentSelectedUtil);
        }
    }, [props.onUtilTabChange, selectedUtilIndex, selectedUtilName]);

    useImperativeHandle(ref, () => ({
        selectTab: selectTab,
        refreshAll: refreshAll,
        refreshBadges: refreshBadges,
        getSelectedTab: getSelectedTab,
        focusSearchBox: focusSearchBox,
        patchTabRows: patchTabRows,
        getTabSearchConditions: getTabSearchConditions,
        refreshTabRows: refreshTabRows,
        isTabReady: isTabReady
    }));

    useEffect(() => {
        return () => {
            for (let i = 0; i < tabRefs.length; i++) {
                delete tabRefs[i].ref;
            }
        };
    }, []);

    if (utils.length === 1) return <div className={'ant-pos-screen-utils'}>{itemRender(utils[0])}</div>;

    return (
        <TabPanel
            className={['ant-pos-screen-utils', utils.length === 1 ? 'ant-pos-screen-utils-hidden' : ''].join(' ')}
            stylingUtil={'primary'}
            itemTitleRender={itemTitleRender}
            itemRender={itemRender}
            animationEnabled={true}
            swipeEnabled={false}
            focusStateEnabled={false}
            iconPosition={'top'}
            // selectedItem={selectedUtil}
            selectedIndex={selectedUtilIndex}
            items={utils}
            onSelectionChanged={onSelectionChanged}
            deferRendering={true}
        />
    );
});
